/**
 * AI:
 * Status: "confirmed"
 * Type: Component
 * SubType: CrudIntegration
 * Reason: ObjectBaseComponent for LogicFlowExecution entity — displays execution status/mode badges and generic CRUD form.
 */
import {Component, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {ConfirmationService} from 'primeng/api';
import {ObjectBaseComponent} from '../../../../../core/shared/object/mvs-object-base/object-base.component';
import {MvsCoreService} from '../../../../../core/shared/service/mvs-core.service';
import {MvsMessageService} from '../../../../../core/shared/service/message/mvs-message.service';
import {ObserverService} from '../../../../../core/shared/object/service/observer.service';
import {LogicFlowExecutionDto} from '../../../model/dto/entity/logic-flow-execution.dto';
import {LogicFlowExecutionStatusEnum} from '../../../enum/logic-flow-execution-status.enum';
import {LogicFlowExecutionModeEnum} from '../../../enum/logic-flow-execution-mode.enum';

@Component({
    selector: 'mvs-lg-logic-flow-execution-object-component',
    templateUrl: './lg-logic-flow-execution-object.component.html',
    styleUrls: ['./lg-logic-flow-execution-object.component.scss'],
    standalone: false
})
export class LgLogicFlowExecutionObjectComponent extends ObjectBaseComponent implements OnInit, OnChanges, OnDestroy {

    dto: LogicFlowExecutionDto;

    /** Status display metadata */
    statusLabel: string = '';
    statusSeverity: 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' = 'secondary';

    /** Mode display metadata */
    modeLabel: string = '';

    /** Computed duration in milliseconds */
    durationMs: number | null = null;

    constructor(
        protected coreService: MvsCoreService,
        protected messageService: MvsMessageService,
        protected confirmationService: ConfirmationService,
        protected override observerService: ObserverService
    ) {
        super(coreService, messageService, confirmationService, observerService);
    }

    ngOnInit() {
        super.ngOnInit();
    }

    onObjectChanged() {
        this.updateStatusMeta();
        this.updateModeMeta();
        this.computeDuration();
    }

    /**
     * Derive status display metadata (label, severity) from executionStatus.
     */
    private updateStatusMeta(): void {
        switch (this.dto.executionStatus) {
            case LogicFlowExecutionStatusEnum.COMPLETED:
                this.statusLabel = 'Abgeschlossen';
                this.statusSeverity = 'success';
                break;
            case LogicFlowExecutionStatusEnum.FAILED:
                this.statusLabel = 'Fehlgeschlagen';
                this.statusSeverity = 'danger';
                break;
            case LogicFlowExecutionStatusEnum.RUNNING:
                this.statusLabel = 'Läuft';
                this.statusSeverity = 'info';
                break;
            case LogicFlowExecutionStatusEnum.PAUSED:
                this.statusLabel = 'Pausiert';
                this.statusSeverity = 'warn';
                break;
            case LogicFlowExecutionStatusEnum.CREATED:
                this.statusLabel = 'Erstellt';
                this.statusSeverity = 'secondary';
                break;
            default:
                this.statusLabel = 'Unbekannt';
                this.statusSeverity = 'secondary';
                break;
        }
    }

    /**
     * Derive mode display label from executionMode.
     */
    private updateModeMeta(): void {
        switch (this.dto.executionMode) {
            case LogicFlowExecutionModeEnum.NORMAL:
                this.modeLabel = 'Normal';
                break;
            case LogicFlowExecutionModeEnum.DEBUG:
                this.modeLabel = 'Debug';
                break;
            case LogicFlowExecutionModeEnum.DRY_RUN:
                this.modeLabel = 'Testlauf';
                break;
            default:
                this.modeLabel = 'Unbekannt';
                break;
        }
    }

    /**
     * Compute duration from startedAt and completedAt timestamps.
     */
    private computeDuration(): void {
        if (this.dto.startedAt && this.dto.completedAt) {
            const start = new Date(this.dto.startedAt).getTime();
            const end = new Date(this.dto.completedAt).getTime();
            this.durationMs = end - start;
        } else {
            this.durationMs = null;
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }
}
